/**
 * components/Onboarding/components/ResumePrompt.tsx
 * Resume prompt for returning users with onboarding progress
 *
 * Displays a Card-based prompt allowing users to either continue
 * their previous onboarding session or start fresh.
 */

// 1. External dependencies
import React from 'react';
import { RefreshCcw } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardFooter, Button } from '@enfomentis/ui';

/**
 * Props for the ResumePrompt component
 */
export interface ResumePromptProps {
  /** Handler when user chooses to continue from where they left off */
  onContinue: () => void;
  /** Handler when user chooses to start fresh (clears draft) */
  onStartFresh: () => void;
  /** The screen name to show in the prompt (e.g., "Account Details") */
  lastScreenName?: string | undefined;
  /** Loading state during Start Fresh cleanup (disables buttons, shows spinner) */
  isLoading?: boolean;
}

/**
 * Resume prompt component for returning users
 *
 * Displays a centered Card with two options:
 * - Continue where you left off (primary action)
 * - Start fresh (secondary action)
 */
export const ResumePrompt: React.FC<ResumePromptProps> = ({
  onContinue,
  onStartFresh,
  lastScreenName,
  isLoading = false
}) => {
  const titleId = 'resume-prompt-title';
  const descriptionId = 'resume-prompt-description';

  // Build description text based on whether lastScreenName is provided
  const descriptionText = lastScreenName
    ? `You left off on "${lastScreenName}". Would you like to pick up where you left off, or start fresh?`
    : 'Would you like to pick up where you left off, or start fresh?';

  return (
    <div className="max-w-md mx-auto px-2">
      <Card
        className="border border-primary"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <CardBody className="p-6">
          <div className="flex flex-col items-center text-center gap-4">
            {/* Icon */}
            <div className="p-3 bg-primary/10 rounded-full">
              <RefreshCcw className="w-8 h-8 text-primary" />
            </div>

            {/* Title and description */}
            <div>
              <h3
                id={titleId}
                className="text-lg font-semibold text-text-primary mb-2"
              >
                Welcome Back
              </h3>
              <p
                id={descriptionId}
                className="text-sm text-text-secondary"
              >
                {descriptionText}
              </p>
            </div>
          </div>
        </CardBody>

        <CardFooter className="flex flex-col gap-3 pt-0">
          <Button
            color="primary"
            className="w-full min-h-[44px]"
            onPress={onContinue}
            isDisabled={isLoading}
          >
            Continue where you left off
          </Button>
          <Button
            color="default"
            variant="flat"
            className="w-full min-h-[44px]"
            onPress={onStartFresh}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {isLoading ? 'Cleaning up…' : 'Start fresh'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
